import {v4 as uuidv4} from "uuid";
import React from "react";
import {Variant} from "@mui/material/styles/createTypography";

export interface RichTextProps {
    textAsTree: RichTextElement,
    propertyName?: string,
    variant?: Variant,
    headlineElement?: string, // Element that should be rendered as headline
    wordsLimit?: number,
    charLimit?: number,
    noLinks?: boolean,
    strong?: boolean,
    color?: string,
    alignContent?: "center" | "right" | "left" | "inherit" | "justify" | undefined,
    collapseOnDoubleBreak?: boolean,
    docType?: "news" | "pressrelease" | "publication" | "article" | undefined,
    renderDownloadAsLink?: boolean,
    id?: string,
    isHideNavi?: boolean,
    metadataId?: string
}

export interface RichTextElement {
    _type: string
    name?: string
    attributes?: Array<Attribute>
    children?: Array<RichTextElement>
    data?: string
    uuid?: string
}

export interface Attribute {
    _type: string;
    name: string;
    value: string;
}

export type ListType = 'ul' | 'ol';

export function enrichWithUuid(element: any): boolean {
    if (!element) {
        return false;
    }
    element.uuid = uuidv4();
    let fullyEnriched = true;

    // Recursively add UUID to children if they exist
    if (element.children) {
        (element.children as any[]).forEach(child => {
            fullyEnriched = enrichWithUuid(child) && fullyEnriched;
        });
    }

    return fullyEnriched;
}
export function renderTruncatedElement(props: RichTextProps,
                                       renderElement: (...args: any[]) => JSX.Element | null,
                                       getTextChild: (...args: any[]) => string,
                                       showMore: boolean,
                                       setShowMore: (...args: any[]) => void,
                                       parentComponentId: string,
                                       pageId: string): JSX.Element {
    if (!props.textAsTree) {
        return <React.Fragment />;
    }

    const children: (JSX.Element | null)[] = [];
    let childTruncated: boolean = false;
    let charCount = 0;
    (props.textAsTree!.children ?? []).forEach((child: RichTextElement) => {
        if (!childTruncated) {
            const childElement = renderElement(props, child, parentComponentId, pageId, undefined, props.variant, showMore, setShowMore)
            const childText = getTextChild(childElement)
            charCount += childText.length
            children.push(childElement)
            if (charCount > props.charLimit!) {
                childTruncated = true
            }
        }
    })
    return <React.Fragment key={props.textAsTree.uuid}>
        {children}
    </React.Fragment>
}

export function getElementTextAlign(props: RichTextProps, element: RichTextElement) {
    const className = getAttribute(element.attributes!, "class")
    if (className && isAlignClass(className)) {
        return className.split('--')[1] as "center" | "right" | "left" | "justify";
    } else if (props.alignContent) {
        return props.alignContent;
    } else if (element.name!.startsWith('h')) {
        // Headline in article should be centered
        return 'center'
    } else {
        return "left";
    }
}

export function getAttribute(attributes: Array<Attribute>, key: string) {
    return attributes?.filter(a => a.name === key).map(a => a.value)[0] as string
}

export function findConsecutiveBrChildIndex(element: RichTextElement): number | undefined {
    const children = element.children;
    if (!children) return undefined;

    const index = children.findIndex((currentChild, currentIndex) => {
        const nextChild = children[currentIndex + 1] as RichTextElement | undefined;
        return currentChild.name === 'br' && nextChild?.name === 'br';
    });

    return index !== -1 ? index : undefined;
}

export function getTextChild(element: JSX.Element | null): string {
    if (!element) return ''
    if (typeof element.props.children === 'string') {
        return element.props.children;
    } else if (React.Children.count(element.props.children) > 0) {
        let result = '';
        React.Children.forEach(element.props.children, (child: JSX.Element | string) => {
            if (typeof child === 'string') {
                result += " " + child;
            } else {
                result += " " + getTextChild(child);
            }
        });
        return result;
    } else {
        return '';
    }
}

export function elementIncludesImage(element: RichTextElement): boolean {
    return !!(element.children && element.children.some(child => child.name === "img"));
}

export const isUrlAbsolute = (url: string) => {
    return (url.indexOf('://') > 0 || url.indexOf('//') === 0);
}

export const isCtaButtonClass = (className: string) => {
    return ['cta-default', 'cta-blue', 'cta-yellow'].includes(className);
}

export const isAlignClass = (className: string) => {
    return ['align--left', 'align--center', 'align--right', 'align--justify'].includes(className);
}